import { Button, LearnMoreLink } from '@auctisync/ui';
import { COMMON_TIME_ZONES, isValidTimeZone } from '@auctisync/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HouseSelector } from '../../components/HouseSelector';
import { useToast } from '../../components/Toast';
import { useOwnedTenants } from '../../hooks/useOwnedTenants';
import { supabase } from '../../lib/supabase';

/**
 * Tenant admin — House Contact (owner-only).
 *
 * WHY THIS PAGE EXISTS. The bidder app's Contact screen asks a house for its
 * phone number, email, address and opening hours, and the platform held none of
 * those fields anywhere: `auctions` has per-auction pickup details, `profiles`
 * and `consignors` have PERSONAL phone numbers, and `tenants.metadata` held one
 * unrelated key. Without somewhere to type them, the app's Contact screen would
 * have been permanently empty — a form with no way to fill it in is the same
 * ghost feature as a screen with invented details.
 *
 * ⚠️ EVERYTHING ON THIS PAGE IS PUBLIC. `get_house_contact` is granted to
 * `anon` deliberately: this is what a business prints on its own catalogue.
 * Nothing private may be added here. The money-adjacent settings — remit-to
 * address, "make checks payable to" — live on Invoice Settings behind an
 * owner-only reader, and must stay there.
 *
 * Stored in `tenants.metadata.house_contact` via the same owner-only RPC pair
 * as invoice settings, including the same multi-house resolution: an owner of
 * two houses gets a loud error rather than whichever row the planner returned
 * first.
 *
 * Every field is OPTIONAL and blank by default. A house that gives only a phone
 * number gets a Contact screen showing a phone number; we never invent an
 * opening hour on somebody's behalf.
 */

interface HouseAddressFields {
  line1: string;
  line2: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
}

interface HouseContactSettings {
  phone: string;
  email: string;
  website: string;
  address: HouseAddressFields;
  office_hours: string;
  pickup_hours: string;
  /**
   * IANA zone the house's sales run in.
   *
   * 🔴 THIS IS WHAT A NEW SALE INHERITS, and it is the point of asking. Jim,
   * 2026-09-18, about the software he uses today: *"When I am traveling, I have
   * to do calculations to make sure I am entering the right auction times."* A
   * new sale seeded from the BROWSER would be wrong in exactly that situation;
   * seeded from here it is right wherever the laptop is.
   */
  timezone: string;
  note: string;
}

const CAP_SHORT = 200;
const CAP_MEDIUM = 500;

const EMPTY_ADDRESS: HouseAddressFields = {
  line1: '',
  line2: '',
  city: '',
  region: '',
  postal_code: '',
  country: '',
};

const DEFAULT_CONTACT: HouseContactSettings = {
  phone: '',
  email: '',
  website: '',
  address: { ...EMPTY_ADDRESS },
  office_hours: '',
  pickup_hours: '',
  timezone: '',
  note: '',
};

/**
 * Typed bridge for the two RPCs, matching the pattern AdminInvoiceSettings
 * established: the generated `database.ts` union is regenerated centrally, so a
 * narrow fully-typed signature keeps this page honest without widening the
 * shared client.
 */
type ContactRpc = {
  (
    fn: 'get_house_contact',
    args: { p_tenant_id: string | null },
  ): Promise<{ data: unknown; error: { message: string } | null }>;
  (
    fn: 'set_house_contact',
    args: { p_contact: HouseContactSettings; p_tenant_id: string | null },
  ): Promise<{ data: unknown; error: { message: string } | null }>;
};
const contactRpc = supabase.rpc.bind(supabase) as unknown as ContactRpc;

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Coerce the stored jsonb into a fully-defaulted, well-typed object. */
function normalize(raw: unknown): HouseContactSettings {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const a = (o['address'] && typeof o['address'] === 'object' ? o['address'] : {}) as Record<
    string,
    unknown
  >;
  return {
    phone: str(o['phone']),
    email: str(o['email']),
    website: str(o['website']),
    address: {
      line1: str(a['line1']),
      line2: str(a['line2']),
      city: str(a['city']),
      region: str(a['region']),
      postal_code: str(a['postal_code']),
      country: str(a['country']),
    },
    office_hours: str(o['office_hours']),
    pickup_hours: str(o['pickup_hours']),
    // A zone the runtime does not know reads as unset rather than reaching
    // `Intl`, which throws on an unknown zone instead of degrading.
    timezone: isValidTimeZone(str(o['timezone'])) ? str(o['timezone']) : '',
    note: str(o['note']),
  };
}

/** Trim to the caps before saving — the DB takes whatever it is sent. */
function capForSave(c: HouseContactSettings): HouseContactSettings {
  const cap = (v: string, n: number) => v.trim().slice(0, n);
  return {
    phone: cap(c.phone, CAP_SHORT),
    email: cap(c.email, CAP_SHORT),
    website: cap(c.website, CAP_SHORT),
    address: {
      line1: cap(c.address.line1, CAP_SHORT),
      line2: cap(c.address.line2, CAP_SHORT),
      city: cap(c.address.city, CAP_SHORT),
      region: cap(c.address.region, CAP_SHORT),
      postal_code: cap(c.address.postal_code, CAP_SHORT),
      country: cap(c.address.country, CAP_SHORT),
    },
    office_hours: cap(c.office_hours, CAP_SHORT),
    pickup_hours: cap(c.pickup_hours, CAP_SHORT),
    timezone: c.timezone.trim(),
    note: cap(c.note, CAP_MEDIUM),
  };
}

export function AdminHouseContact() {
  const toast = useToast();
  const owned = useOwnedTenants();
  const [contact, setContact] = useState<HouseContactSettings>(DEFAULT_CONTACT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedTenantId = owned.selectedId;

  // Latest-wins guard, same reason as Invoice Settings: switching houses faster
  // than the RPC resolves must not let a stale response paint the previous
  // house's details, which the next save would then write to the new house.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    const { data, error: err } = await contactRpc('get_house_contact', {
      p_tenant_id: selectedTenantId,
    });
    if (seq !== loadSeq.current) return;
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    setContact(normalize(data));
    setLoading(false);
  }, [selectedTenantId]);

  useEffect(() => {
    if (owned.loading) return;
    void load();
  }, [load, owned.loading]);

  async function save() {
    setSaving(true);
    setError(null);
    const payload = capForSave(contact);
    const { error: err } = await contactRpc('set_house_contact', {
      p_contact: payload,
      p_tenant_id: selectedTenantId,
    });
    setSaving(false);
    if (err) {
      setError(
        err.message.includes('42501') || /owner/i.test(err.message)
          ? 'House contact details are available to the tenant owner.'
          : err.message,
      );
      toast.error('House contact could not be saved.');
      return;
    }
    setContact(payload);
    toast.success('House contact saved.');
  }

  function setTop<K extends keyof Omit<HouseContactSettings, 'address'>>(key: K, value: string) {
    setContact((prev) => ({ ...prev, [key]: value }));
  }
  function setAddr(key: keyof HouseAddressFields, value: string) {
    setContact((prev) => ({ ...prev, address: { ...prev.address, [key]: value } }));
  }

  if (loading) return <div style={wrapStyle}>Loading house contact…</div>;

  return (
    <div style={wrapStyle}>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.75rem',
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
          }}
        >
          House Contact
        </h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.3rem' }}>
          How bidders reach you. These appear on the Contact screen in the bidder app. Anything you
          leave blank is simply not shown — nothing is invented on your behalf.{' '}
          <LearnMoreLink helpSlug="house-contact-details" newTab>
            What bidders see
          </LearnMoreLink>
        </p>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.4rem', fontSize: '0.85rem' }}>
          <strong>These details are public.</strong> Bidders and visitors can see them. Bank details
          and remit-to addresses belong on Invoice Settings, not here.
        </p>
      </header>

      <HouseSelector
        tenants={owned.tenants}
        selectedId={owned.selectedId}
        onSelect={owned.setSelectedId}
        disabled={saving}
      />

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      <section style={cardStyle}>
        <SectionHead
          title="Getting in touch"
          description="A bidder taps the number to call it, so write it the way it dials."
        />
        <Field label="Phone">
          <input
            type="tel"
            value={contact.phone}
            maxLength={CAP_SHORT}
            onChange={(e) => setTop('phone', e.target.value)}
            placeholder="(417) 555-0148"
            aria-label="Phone"
            style={inputStyle}
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            value={contact.email}
            maxLength={CAP_SHORT}
            onChange={(e) => setTop('email', e.target.value)}
            placeholder="office@yourhouse.com"
            aria-label="Email"
            style={inputStyle}
          />
        </Field>
        <Field label="Website" hint="optional">
          <input
            type="url"
            value={contact.website}
            maxLength={CAP_SHORT}
            onChange={(e) => setTop('website', e.target.value)}
            placeholder="https://yourhouse.com"
            aria-label="Website"
            style={inputStyle}
          />
        </Field>
      </section>

      <section style={cardStyle}>
        <SectionHead
          title="Where you are"
          description="Your public premises. Per-auction pickup addresses are set on the auction itself."
        />
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          <input
            type="text"
            value={contact.address.line1}
            maxLength={CAP_SHORT}
            onChange={(e) => setAddr('line1', e.target.value)}
            placeholder="Address line 1"
            aria-label="Address line 1"
            style={inputStyle}
          />
          <input
            type="text"
            value={contact.address.line2}
            maxLength={CAP_SHORT}
            onChange={(e) => setAddr('line2', e.target.value)}
            placeholder="Address line 2 (optional)"
            aria-label="Address line 2"
            style={inputStyle}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.5rem' }}>
            <input
              type="text"
              value={contact.address.city}
              maxLength={CAP_SHORT}
              onChange={(e) => setAddr('city', e.target.value)}
              placeholder="City"
              aria-label="City"
              style={inputStyle}
            />
            <input
              type="text"
              value={contact.address.region}
              maxLength={CAP_SHORT}
              onChange={(e) => setAddr('region', e.target.value)}
              placeholder="State / region"
              aria-label="State or region"
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <input
              type="text"
              value={contact.address.postal_code}
              maxLength={CAP_SHORT}
              onChange={(e) => setAddr('postal_code', e.target.value)}
              placeholder="Postal code"
              aria-label="Postal code"
              style={inputStyle}
            />
            <input
              type="text"
              value={contact.address.country}
              maxLength={CAP_SHORT}
              onChange={(e) => setAddr('country', e.target.value)}
              placeholder="Country"
              aria-label="Country"
              style={inputStyle}
            />
          </div>
        </div>
      </section>

      <section style={cardStyle}>
        <SectionHead
          title="When you are open"
          description="Free text, because opening hours are full of exceptions. Say it the way you would on the phone."
        />
        <Field label="Office hours">
          <input
            type="text"
            value={contact.office_hours}
            maxLength={CAP_SHORT}
            onChange={(e) => setTop('office_hours', e.target.value)}
            placeholder="Mon–Fri 9–5"
            aria-label="Office hours"
            style={inputStyle}
          />
        </Field>
        <Field
          label="Your time zone"
          hint="new sales start here — each sale can still be set to its own"
        >
          <select
            value={contact.timezone}
            onChange={(e) => setTop('timezone', e.target.value)}
            aria-label="Time zone"
            style={inputStyle}
          >
            <option value="">— Not set —</option>
            {COMMON_TIME_ZONES.map((z) => (
              <option key={z.id} value={z.id}>
                {z.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Pickup window" hint="the general one — an auction can still set its own">
          <input
            type="text"
            value={contact.pickup_hours}
            maxLength={CAP_SHORT}
            onChange={(e) => setTop('pickup_hours', e.target.value)}
            placeholder="Sat 9–4"
            aria-label="Pickup window"
            style={inputStyle}
          />
        </Field>
        <Field label="Anything else a bidder should know" hint="optional">
          <textarea
            value={contact.note}
            maxLength={CAP_MEDIUM}
            onChange={(e) => setTop('note', e.target.value)}
            placeholder="Call ahead if you are collecting anything that needs a forklift."
            aria-label="Note"
            rows={3}
            style={textareaStyle}
          />
        </Field>
      </section>

      <Button onClick={() => void save()} disabled={saving}>
        {saving ? 'Saving…' : 'Save house contact'}
      </Button>
    </div>
  );
}

// ── Presentational helpers (mirrors AdminInvoiceSettings) ───────────────────

function SectionHead({ title, description }: { title: string; description: string }) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
        {title}
      </h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.15rem 0 0' }}>
        {description}
      </p>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'block', marginBottom: '0.75rem' }}>
      <span
        style={{
          display: 'block',
          fontSize: '0.85rem',
          fontWeight: 500,
          color: 'var(--text)',
          marginBottom: '0.3rem',
        }}
      >
        {label}
        {hint && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> — {hint}</span>}
      </span>
      {children}
    </label>
  );
}

const wrapStyle: React.CSSProperties = {
  padding: '2rem 2.25rem',
  maxWidth: '56rem',
};

const cardStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  background: 'var(--surface-elev)',
  marginBottom: '1rem',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  marginBottom: '1rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '0.45rem 0.6rem',
  fontSize: '0.9rem',
  color: 'var(--text)',
  background: 'var(--surface)',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  fontFamily: 'inherit',
  resize: 'vertical',
};
